<template>
  <teleport to="body">
    <div
      class="modal fade"
      ref="modal"
      tabindex="-1"
      data-bs-backdrop="static"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">

          <div class="modal-header border-0 pb-2 bg-top-nav" style="border-radius: 16px 16px 0 0;">
            <h5 class="modal-title fw-bold text-white d-flex align-items-center flex-wrap gap-2">
              <span><i class="fa-solid fa-gift me-2" />{{ $t('_loyalty.Choose Reward') }}</span>
              <span class="badge bg-white text-theme">{{ $t(rewardName) }}</span>
            </h5>
            <button type="button" class="btn-close btn-close-white shadow-none" data-bs-dismiss="modal" />
          </div>

          <div class="modal-body px-3 py-2">
            <!-- loading -->
            <div v-if="fetching" class="text-center py-5 text-muted">
              <i class="fa-solid fa-spinner fa-spin fa-2x" />
            </div>

            <template v-else>
              <!-- product -->
              <div class="d-flex align-items-center gap-3 pb-3 border-bottom mb-2">
                <div
                  v-if="displayImage"
                  class="flex-shrink-0 rounded-3 bg-light overflow-hidden"
                  style="width: 64px; height: 64px;"
                >
                  <img :src="displayImage" class="w-100 h-100" style="object-fit: cover;" @error="$storeIndex.onImageError">
                </div>
                <div class="flex-grow-1">
                  <h6 class="fw-bold mb-1">{{ $t(displayName) }}</h6>
                  <span class="badge bg-success-subtle text-success">{{ $t('Free') }}</span>
                  <span v-if="selectedVariant && (!selectedVariant.is_in_stock || selectedVariant.is_ingredient_insufficient)" class="badge bg-dark ms-1">
                    {{ $t('Sold out') }}
                  </span>
                </div>
              </div>

              <!-- attributes (size / meat type ... → map variant) -->
              <div v-for="attribute in product.attributes" :key="attribute.id" class="mb-3">
                <label class="form-label small text-muted fw-bold">
                  <i class="fa-solid fa-layer-group me-1 text-theme" />{{ $ln(attribute) }}
                  <span class="text-danger">*</span>
                </label>
                <div class="d-flex flex-wrap gap-2">
                  <template v-for="option in attribute.options" :key="option.id">
                    <button
                      v-if="isAttributeOptionAvailable(attribute.id, option.id)"
                      type="button"
                      class="btn btn-sm rounded-pill px-3 fw-semibold"
                      :class="attributeSelections[attribute.id] === option.id ? 'btn-theme text-white' : 'btn-outline-secondary'"
                      @click="selectAttributeOption(attribute.id, option.id)"
                    >
                      {{ $ln(option) }}
                    </button>
                  </template>
                </div>
              </div>

              <!-- modifiers (of the selected variant) -->
              <div v-for="modifier in activeModifiers" :key="modifier.id" class="mb-3">
                <label class="form-label small text-muted fw-bold">
                  <i class="fa-solid fa-sliders me-1 text-theme" />{{ $ln(modifier) }}
                  <span v-if="modifier.is_required" class="text-danger">*</span>
                  <span class="fw-normal text-muted">({{ modifierHint(modifier) }})</span>
                </label>
                <div class="d-flex flex-wrap gap-2">
                  <button
                    v-for="option in modifier.options"
                    :key="option.id"
                    type="button"
                    class="btn btn-sm rounded-pill"
                    :class="isOptionSelected(modifier, option.id) ? 'btn-theme text-white' : 'btn-outline-secondary'"
                    :disabled="!option.is_in_stock || option.is_ingredient_insufficient"
                    @click="toggleOption(modifier, option.id)"
                  >
                    {{ $ln(option) }}
                    <span
                      v-if="parseFloat(option.price_adjustment) > 0"
                      class="ms-1"
                      :class="isOptionSelected(modifier, option.id) ? 'text-white opacity-75' : 'text-theme fw-semibold'"
                    >+{{ $storeIndex.formatThaiCurrency(option.price_adjustment) }}</span>
                    <span v-if="!option.is_in_stock || option.is_ingredient_insufficient" class="ms-1">({{ $t('Sold out') }})</span>
                  </button>
                </div>
              </div>

              <div v-if="product.attributes && product.attributes.length && !selectedVariant" class="text-muted text-center py-2" style="font-size: 13px;">
                {{ $t('Please select an option first') }}
              </div>
            </template>
          </div>

          <div class="modal-footer border-0 pt-0 px-3 pb-3 d-block">
            <!-- ฐานสินค้าฟรี — ส่วนเสริมที่มีราคาต้องจ่ายเพิ่ม -->
            <div class="d-flex justify-content-between align-items-center mb-2 small">
              <span class="text-muted">
                <i class="fa-solid fa-circle-info me-1" />{{ addonTotal > 0 ? $t('Paid add-ons') : $t('Free') }}
              </span>
              <span class="fw-bold" :class="addonTotal > 0 ? 'text-theme' : 'text-success'">
                {{ addonTotal > 0 ? '+' + $storeIndex.formatThaiCurrency(addonTotal) : $t('Free') }}
              </span>
            </div>
            <ladda
              text="Redeem"
              :btn-class="'btn btn-theme w-100 py-3 fw-bold text-white'"
              style="border-radius: 12px;"
              :loading="loading"
              icon="<i class='fa-solid fa-gift'></i>"
              :disabled="fetching || !canConfirm"
              @click="confirm"
            />
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="js">
import { POSProducts } from '@/services/endpoint/pos.service';
import Ladda from '@/plugins/Ladda.vue';

export default {
  name: 'RewardSelectModal',
  components: { Ladda },
  props: {
    rewardProductId: { type: [Number, String], required: true },
    rewardName: { type: String, default: '' },
    loading: { type: Boolean, default: false },
  },
  emits: ['confirm', 'close'],
  data() {
    return {
      fetching: true,
      product: { name: '', price: 0, attributes: [], variants: [], modifiers: [], image_url: null, thumbnail_url: null },
      attributeSelections: {},
      // { [modifierId]: number | number[] }
      modifierSelections: {},
    };
  },
  computed: {
    selectedVariant() {
      if (!this.product.variants.length || !this.product.attributes.length) return null;
      const ids = Object.values(this.attributeSelections);
      if (ids.length !== this.product.attributes.length) return null;
      return this.product.variants.find(v =>
        v.option_ids && v.option_ids.length === ids.length && ids.every(id => v.option_ids.includes(id))
      ) || null;
    },
    activeModifiers() {
      if (this.selectedVariant) return this.selectedVariant.modifiers || [];
      return this.product.attributes.length ? [] : (this.product.modifiers || []);
    },
    displayName() {
      return this.selectedVariant ? this.selectedVariant.name : this.product.name;
    },
    displayImage() {
      const v = this.selectedVariant;
      if (v) return v.image_url || v.thumbnail_url || this.product.image_url || this.product.thumbnail_url;
      return this.product.image_url || this.product.thumbnail_url;
    },
    productId() {
      return this.selectedVariant ? this.selectedVariant.id : this.product.id;
    },
    // ยอดส่วนเสริมที่มีราคา (modifier ที่เลือกและ price_adjustment > 0) = ส่วนที่ลูกค้าจ่าย
    addonTotal() {
      let sum = 0;
      for (const m of this.activeModifiers) {
        const sel = this.modifierSelections[m.id];
        if (!sel) continue;
        (Array.isArray(sel) ? sel : [sel]).forEach(id => {
          const o = (m.options || []).find(x => x.id === id);
          if (o) sum += parseFloat(o.price_adjustment) || 0;
        });
      }
      return sum;
    },
    canConfirm() {
      // ต้องเลือก variant ครบถ้าสินค้ามี attribute
      if (this.product.attributes && this.product.attributes.length && !this.selectedVariant) return false;
      if (this.selectedVariant && (!this.selectedVariant.is_in_stock || this.selectedVariant.is_ingredient_insufficient)) return false;
      // required modifier ต้องครบ
      for (const m of this.activeModifiers) {
        if (!m.is_required) continue;
        const sel = this.modifierSelections[m.id];
        if (m.allow_multiple) {
          if (!sel || !sel.length) return false;
          if (m.min_options > 0 && sel.length < m.min_options) return false;
        } else if (!sel) {
          return false;
        }
      }
      return true;
    },
  },
  watch: {
    selectedVariant(nv, ov) {
      if (nv?.id !== ov?.id) {
        this.modifierSelections = {};
        this.initDefaultModifiers();
      }
    },
  },
  methods: {
    isAttributeOptionAvailable(attributeId, optionId) {
      const test = { ...this.attributeSelections, [attributeId]: optionId };
      const ids = Object.values(test);
      return this.product.variants.some(v => ids.every(id => v.option_ids.includes(id)));
    },
    selectAttributeOption(attributeId, optionId) {
      this.attributeSelections = { ...this.attributeSelections, [attributeId]: optionId };
    },
    modifierHint(modifier) {
      // ไม่บังคับ → บอกชัดว่า optional; บังคับ → บอกจำนวนที่ต้องเลือก
      if (!modifier.is_required) return this.$t('optional');
      if (modifier.allow_multiple && modifier.max_options > 0) {
        return this.$t('Select') + ' ' + modifier.min_options + '-' + modifier.max_options;
      }
      return this.$t('Select') + ' 1';
    },
    isOptionSelected(modifier, optionId) {
      const sel = this.modifierSelections[modifier.id];
      return modifier.allow_multiple ? (sel || []).includes(optionId) : sel === optionId;
    },
    toggleOption(modifier, optionId) {
      if (!modifier.allow_multiple) {
        // single → toggle off ถ้ากดซ้ำ (เว้นแต่ required ค่อยคงไว้)
        const cur = this.modifierSelections[modifier.id];
        this.modifierSelections = { ...this.modifierSelections, [modifier.id]: cur === optionId && !modifier.is_required ? null : optionId };
        return;
      }
      const cur = this.modifierSelections[modifier.id] || [];
      if (cur.includes(optionId)) {
        this.modifierSelections = { ...this.modifierSelections, [modifier.id]: cur.filter(id => id !== optionId) };
        return;
      }
      if (modifier.max_options > 0 && cur.length >= modifier.max_options) return;
      this.modifierSelections = { ...this.modifierSelections, [modifier.id]: [...cur, optionId] };
    },
    initDefaultAttributes() {
      if (!this.product.attributes.length || !this.product.variants.length) return;
      const first = this.product.variants.find(v => v.is_in_stock && !v.is_ingredient_insufficient) || this.product.variants[0];
      if (!first || !first.option_ids) return;
      const sel = {};
      for (const attr of this.product.attributes) {
        const match = attr.options.find(o => first.option_ids.includes(o.id));
        if (match) sel[attr.id] = match.id;
      }
      this.attributeSelections = sel;
    },
    initDefaultModifiers() {
      this.$nextTick(() => {
        const sel = {};
        for (const m of this.activeModifiers) {
          const defaults = m.options.filter(o => o.is_default && o.is_in_stock && !o.is_ingredient_insufficient);
          if (!defaults.length) continue;
          sel[m.id] = m.allow_multiple ? defaults.map(o => o.id) : defaults[0].id;
        }
        this.modifierSelections = sel;
      });
    },
    confirm() {
      if (!this.canConfirm) return;
      const optionIds = [];
      for (const m of this.activeModifiers) {
        const sel = this.modifierSelections[m.id];
        if (!sel || (Array.isArray(sel) && !sel.length)) continue;
        (Array.isArray(sel) ? sel : [sel]).forEach(id => optionIds.push(id));
      }
      this.$emit('confirm', { product_id: this.productId, modifier_option_ids: optionIds });
    },
    close() {
      $(this.$refs.modal).modal('hide');
    },
    async getInfo() {
      this.fetching = true;
      try {
        const res = await POSProducts.info(this.rewardProductId);
        this.product = { attributes: [], variants: [], modifiers: [], ...res.data.data };
        this.initDefaultAttributes();
        this.initDefaultModifiers();
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Failed to load'));
        this.close();
      } finally {
        this.fetching = false;
      }
    },
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    this.getInfo();
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  },
};
</script>
